/**
 * Tile component - represents a single letter tile
 */

import { motion } from 'framer-motion';
import { isVowel } from '../../utils/game';
import { useSettingsStore } from '../../stores/settingsStore';
import clsx from 'clsx';

interface TileProps {
  letter: string;
  isNew?: boolean;
  small?: boolean;
  tiny?: boolean;
  onClick?: () => void;
  selected?: boolean;
  highlighted?: boolean;  // True when this tile is being used by current typing
}

export function Tile({
  letter,
  isNew = false,
  small = false,
  tiny = false,
  onClick,
  selected = false,
  highlighted = false,
}: TileProps) {
  const colorblindMode = useSettingsStore((s) => s.colorblindMode);
  const reducedMotion = useSettingsStore((s) => s.reducedMotion);
  const vowel = isVowel(letter);

  // Enhanced 3D flip animation with spring physics
  const baseVariants = {
    initial: reducedMotion
      ? { opacity: 0 }
      : {
          rotateX: -90,
          scale: 0.6,
          y: -20,
          opacity: 0,
        },
    animate: reducedMotion
      ? { opacity: 1 }
      : {
          rotateX: 0,
          scale: 1,
          y: 0,
          opacity: 1,
        },
  };

  // Spring transition for more physical feel
  const springTransition = reducedMotion
    ? { duration: 0.15 }
    : {
        type: 'spring',
        stiffness: 400,
        damping: 25,
        mass: 1,
      };

  return (
    <motion.div
      className={clsx(
        'tile',
        vowel ? 'tile--vowel' : 'tile--consonant',
        tiny && 'tile--tiny',
        small && !tiny && 'tile--small',
        isNew && 'tile--new',
        selected && 'ring-2 ring-blue-500',
        highlighted && 'ring-[3px] ring-blue-400 dark:ring-blue-300 ring-offset-2 ring-offset-white dark:ring-offset-slate-900 shadow-[0_0_20px_6px_rgba(59,130,246,0.6)] dark:shadow-[0_0_25px_8px_rgba(96,165,250,0.7)] z-10',
        onClick && 'cursor-pointer hover:scale-105'
      )}
      variants={isNew ? baseVariants : undefined}
      initial={isNew ? 'initial' : false}
      animate={isNew ? 'animate' : highlighted ? { scale: 1.1, y: -2 } : 'animate'}
      transition={springTransition}
      style={{ transformStyle: 'preserve-3d', perspective: 1000 }}
      onClick={onClick}
      role={onClick ? 'button' : 'img'}
      aria-label={`${letter}, ${vowel ? 'vowel' : 'consonant'}${highlighted ? ', being used' : ''}`}
      tabIndex={onClick ? 0 : undefined}
    >
      <span className="tile__letter">{letter}</span>
      {colorblindMode && (
        <span className="tile__symbol" aria-hidden="true">
          {vowel ? '●' : '◆'}
        </span>
      )}
    </motion.div>
  );
}
